// Board component — renders the full game board with all plates and gradient
// defs. Shared by Ball Sort and Sortaballs; the palette and CSS/defs prefixes
// are bound per game by thin wrappers.

import { memo, useEffect, useState } from "react"

import { Ball } from "./Ball"
import "./board-geometry.css"
import {
  ballLeft,
  ballSlotTop,
  DEFAULT_SIZING,
  FLIGHT_SINGLE_MS,
  ROW_GAP,
  rowHeight,
  sizingClass,
  type Sizing,
} from "./geometry"

// E2E hook: an opt-in URL param (`?skip-flight=1`) collapses the flight
// transition so long solution replays (the 52-move win-path spec) don't fight
// real-time animation under a loaded dev server. Flight completion is
// JS-gated (setTimeout + transitionend on this duration), so this is the only
// way to truly skip it. Only that test navigates with the param; specs that
// assert animation behavior use plain routes and keep real timing.
const SKIP_FLIGHT_ANIMATIONS =
  new URLSearchParams(window.location.search).get("skip-flight") === "1"

// E2E hook: an opt-in scale (`?slow-flight=N`, or VITE_SLOW_FLIGHT) stretches
// the ball flight so stacked user actions are easy to reproduce in tests.
// `?slow-flight=6` makes a single flight ~3.6s — enough to queue several moves
// before the first one lands.
const FLIGHT_SCALE = (()=> {
  const fromUrl = Number(
    new URLSearchParams(window.location.search).get("slow-flight"),
  )
  const fromEnv = Number(import.meta.env.VITE_SLOW_FLIGHT)
  return Number.isFinite(fromUrl) && fromUrl > 0
    ? fromUrl
    : Number.isFinite(fromEnv) && fromEnv > 0
      ? fromEnv
      : 1
})()

export const getFlightDuration = (groupSize: number): number=> {
  if (SKIP_FLIGHT_ANIMATIONS) return 0
  // Group moves keep the one-after-the-other beat but 30% quicker per ball
  // (geometry.ts FLIGHT_SINGLE_MS: 600ms - 30% = 420ms). The groupSize was
  // previously ignored, so every flight ran at 600ms regardless of size —
  // code and the FLIGHT_SINGLE_MS doc disagreed; this honors the doc.
  const perBall = groupSize > 1 ? FLIGHT_SINGLE_MS * 0.7 : FLIGHT_SINGLE_MS
  return Math.round(perBall * FLIGHT_SCALE)
}

import type { GameState } from "../sortaballs-8-colors/logic"
import type { MovingBall, Palette } from "./types"

import { rangeMap } from "../modules/utils/range-map"
import { prefersReducedMotion } from "../modules/utils/prefers-reduced-motion"
import { Plate } from "./Plate"
import "./board-geometry.css"

export interface BoardProps {
  board: GameState
  // Uniform plate capacity (all existing games); `caps` takes over when given.
  capacity?: number
  // Per-plate capacities for mixed-height boards (e.g. 5 plates of height 6 +
  // 4 plates of height 5). Missing entries fall back to `capacity`.
  caps?: readonly number[]
  // CSS class namespace of the enclosing game ("bs" | "sa").
  cssPrefix: string
  flightKey?: number
  groupSize?: number
  holdCueIndex?: number
  movingBall: MovingBall | null
  onMovingBallDone: ()=> void
  onPlateClick: (index: number)=> void
  onPlateLongPress: (index: number)=> void
  palette: Palette
  selectedPlate: null | number
  selectionCount?: number
  sizing?: Sizing
  // Overrides the automatic row wrap: 10-plate boards default to 5 per row,
  // but the 3-colours game lays out 6 short plates on top and 4 high below.
  platesPerRow?: number
}

// Position of a ball slot in board coordinates — in `--sb-unit` multiples, so
// the flying ball lines up with the plate graphics after both multiply by the
// same unit.
interface BallPositionInput {
  capacity: number
  index: number
  rowHeight: number
  sizing: Sizing
  slot: number
  platesPerRow: number
}

interface Point {
  x: number
  y: number
}

export const ballSlotPoint = (input: BallPositionInput): Point=> {
  const { capacity, index, rowHeight, sizing, slot, platesPerRow } = input
  return {
    x: (index % platesPerRow) * sizing.plateSpacing + ballLeft(sizing),
    y:
      Math.floor(index / platesPerRow) * (rowHeight + ROW_GAP) +
      ballSlotTop(capacity, slot, sizing),
  }
}

// Memoized: plates are individually memoized and the game shell passes stable
// callbacks, so Board only re-renders its rows when a prop that actually moves
// changes (board, movingBall, selection, flightKey, holdCueIndex) — renders of
// SortaballsGame for unrelated reasons (isPlaying flips, header/tour state)
// no longer force the whole plate tree.
export const Board = memo(function Board({
  board,
  capacity = 4,
  caps,
  cssPrefix,
  flightKey = 0,
  groupSize = 1,
  holdCueIndex = -1,
  movingBall,
  onMovingBallDone,
  onPlateClick,
  onPlateLongPress,
  palette,
  selectedPlate,
  selectionCount = 0,
  sizing = DEFAULT_SIZING,
  platesPerRow: requestedPlatesPerRow = 0,
}: BoardProps) {
  // Large boards wrap into rows of 6 (12 plates) or 5 (10 plates); small boards
  // stay on one row. The explicit wrap (3-colours: 6 top, 4 bottom) replaces
  // the automatic 5 per row so 10 plates can split 6+4 instead of 5+5.
  const platesPerRow =
    requestedPlatesPerRow ||
    (board.length > 10 ? 6 : board.length > 6 ? 5 : board.length)
  const rowCount = Math.max(
    0,
    Math.ceil(board.length / Math.max(platesPerRow, 1)),
  )

  // Per-plate capacity: the mixed-height array when given, else the uniform cap.
  const plateCapacityAt = (index: number): number=> caps?.[index] ?? capacity
  // The tallest plate sizes the row; shorter plates keep their own height inside.
  const maxCapacity = caps ? Math.max(...caps) : capacity

  // Every row is as tall as the tallest plate on the board; the flying ball's
  // slot maths needs the same value.
  const rowHeightUnits = rowHeight(maxCapacity, sizing)

  // The shipped geometry (base / compact proportions) as a CSS size class, so
  // the static plates and the flight overlay share one set of --sb-* numbers
  // and the one --sb-unit dvh length.
  const boardSizeClass = sizingClass(sizing)

  return (
    <div
      className={`
        ${cssPrefix}-board-fit
        relative flex w-full justify-center
      `}
    >
      <div
        className={`
          ${cssPrefix}-board
          sb-board
          ${boardSizeClass}
          relative flex flex-col
        `}
      >
        {rangeMap(rowCount, rowIndex=> (
          <div
            className="sb-board-row"
            data-capacity={maxCapacity}
            key={rowIndex}
          >
            {board
              .slice(rowIndex * platesPerRow, (rowIndex + 1) * platesPerRow)
              .map((plate, colIndex)=> {
                const plateIndex = rowIndex * platesPerRow + colIndex
                return (
                  <div className="sb-plate-slot" key={plateIndex}>
                    <Plate
                      balls={plate}
                      capacity={plateCapacityAt(plateIndex)}
                      cssPrefix={cssPrefix}
                      hiddenSlots={
                        movingBall && movingBall.to === plateIndex
                          ? rangeMap(
                              movingBall.count,
                              i=> plate.length - movingBall.count + i,
                            )
                          : undefined
                      }
                      index={plateIndex}
                      isSelected={selectedPlate === plateIndex}
                      // Stable per-game handlers — Plate calls them with its
                      // own index, so no per-plate closure is needed here.
                      onClick={onPlateClick}
                      onLongPress={onPlateLongPress}
                      palette={palette}
                      selectionCount={
                        selectedPlate === plateIndex ? selectionCount : 0
                      }
                      sizing={sizing}
                    />
                    {holdCueIndex === plateIndex && (
                      <span className="hold-cue-badge">HOLD</span>
                    )}
                  </div>
                )
              })}
          </div>
        ))}

        {movingBall?.balls.map((ball, ballIndex)=> (
          <FlyingBall
            ballSize={sizing.ballSize}
            color={ball.color}
            cssPrefix={cssPrefix}
            durationMs={getFlightDuration(groupSize)}
            from={ballSlotPoint({
              capacity: plateCapacityAt(movingBall.from),
              index: movingBall.from,
              rowHeight: rowHeightUnits,
              sizing,
              slot: ball.fromSlot,
              platesPerRow,
            })}
            fromSlot={ball.fromSlot}
            key={`${flightKey}-${ballIndex}`}
            onDone={ballIndex === 0 ? onMovingBallDone : ()=> {}}
            palette={palette}
            to={ballSlotPoint({
              capacity: plateCapacityAt(movingBall.to),
              index: movingBall.to,
              rowHeight: rowHeightUnits,
              sizing,
              slot: ball.toSlot,
              platesPerRow,
            })}
            toSlot={ball.toSlot}
          />
        ))}
      </div>
    </div>
  )
})

// Overlay ball that flies from the source plate to the destination plate.
// Remounts per move (movingBall is replaced each move) so each flight replays.
function FlyingBall({
  ballSize,
  color,
  cssPrefix,
  durationMs,
  from,
  fromSlot,
  onDone,
  palette,
  to,
  toSlot,
}: {
  ballSize?: number
  color: string
  cssPrefix: string
  durationMs: number
  from: Point
  fromSlot: number
  onDone: ()=> void
  palette: Palette
  to: Point
  toSlot: number
}) {
  const [pos, setPos] = useState<Point>(from)

  // Reduced motion: complete the move instantly and explicitly (CSS cannot
  // reliably gate a JS-timed flight, so we cannot depend on the global
  // collapse or on transitionend). Read once — the flight lives ~600ms and
  // the media query only changes via OS settings, never mid-move.
  const reduceMotion = prefersReducedMotion()

  // Start the transition after the initial (source) frame has been painted.
  useEffect(()=> {
    if (reduceMotion) {
      setPos(to)
      onDone()
      return
    }
    const id = requestAnimationFrame(()=> {
      requestAnimationFrame(()=> setPos(to))
    })
    return ()=> cancelAnimationFrame(id)
    // onDone/reduceMotion are stable per flight (remount per move) — [to] is
    // the only changing input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to])

  // Safety net in case the transitionend event is missed — and the ONLY
  // completion path for the 0ms skip-flight e2e transition (a 0-duration
  // transition never fires transitionend). Skipped under reduced motion:
  // onDone already ran; the timeout would only add a phantom hold.
  useEffect(()=> {
    if (reduceMotion) return
    const id = setTimeout(onDone, durationMs + 150)
    return ()=> clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationMs, onDone])

  return (
    <div
      className={`
        ${cssPrefix}-flying-ball
        sb-flying-ball
      `}
      data-from-slot={fromSlot}
      data-to-slot={toSlot}
      onTransitionEnd={reduceMotion ? undefined : onDone}
      style={{
        transform: `translate(calc(var(--sb-unit) * ${pos.x}), calc(var(--sb-unit) * ${pos.y}))`,
        transition: `transform ${durationMs}ms var(--ease-standard)`,
      }}
    >
      <Ball ballSize={ballSize} color={color} palette={palette} />
    </div>
  )
}
